import React, { useEffect, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  Typography,
  IconButton,
  Box,
  Grid,
  Button,
  Switch,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  styled,
  CircularProgress,
} from "@mui/material";
import { Edit, Delete, Add } from "@mui/icons-material";
import NavigationTab from "../navigation";
import { Model } from "./modelinterface";
import AddModelDialog from "./addmodel";
import EditModelDialog from "./updatemodel";
import UploadModelDialog from "./uploadmodel";

const StyledTableCell = styled(TableCell)(() => ({
  fontWeight: "bold",
  color: "white",
}));

const ModelListTable: React.FC = () => {
  const [models, setModels] = useState<Model[]>([]);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [uploaddialogOpen, setUploadDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedModel, setSelectedModel] = useState<Model | null>(null);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [modelToToggle, setModelToToggle] = useState<{
    id: number;
    status: boolean;
  } | null>(null);
  const [loading, setLoading] = useState(false);

  const fetchModels = (currentPage: number) => {
    setLoading(true);
    const token = localStorage.getItem("token");
    if (!token) return;
    fetch(`${process.env.REACT_APP_API_BASE_URL}/vehicleModel`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "x-api-key": token,
      },
      body: JSON.stringify({
        pageNo: currentPage,
        pageSize: pageSize,
      }),
    })
      .then((response) => response.json())
      .then((data) => {
        const sortedModels = (data.items || [])
          .map((item: any) => ({
            id: item.id,
            model_name: item.model_name,
            year: item.year,
            vehicle_photo: item.vehicle_photo,
            created_at: item.created_at,
            updated_at: item.updated_at,
            status: item.status,
          }))
          .sort((a: Model, b: Model) => a.id - b.id);

        setModels(sortedModels);
        setTotalCount(data.totalCount || 0);
      })
      .catch((error) => console.error("Error fetching models:", error))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchModels(page);
  }, [page]);

  const handleNextPage = () => {
    setPage((prevPage) => prevPage + 1);
  };

  const handlePrevPage = () => {
    setPage((prevPage) => Math.max(1, prevPage - 1));
  };

  const handleModelAdded = () => {
    fetchModels(page);
  };

  const handleUploadModel = () => {
    fetchModels(page);
  };

  const handleModelUpdated = () => {
    fetchModels(page);
  };

  const handleEditModel = (model: Model) => {
    setSelectedModel(model);
    setEditDialogOpen(true);
  };

  const handleStatusChangeConfirm = () => {
    if (!modelToToggle) return;

    fetch(
      `https://kineticgreen-backend.navigolabs.com/model/userStatusChangeWTHT/${modelToToggle.id}`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ status: !modelToToggle.status }),
      }
    )
      .then((response) => response.json())
      .then(() => {
        fetchModels(page);
        setStatusDialogOpen(false);
      })
      .catch((error) => console.error("Error updating status:", error));
  };

  const handleStatusChangeRequest = (
    modelId: number,
    currentStatus: boolean
  ) => {
    setModelToToggle({ id: modelId, status: currentStatus });
    setStatusDialogOpen(true);
  };

  return (
    <Box sx={{ background: "#1a1a1a", paddingBottom: 8 }}>
      <NavigationTab />
      <Grid container>
        <Grid
          item
          xs={12}
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            background: "transparent",
          }}
        >
          <Box
            sx={{
              width: "1200px",
              background: "black",
              border: "1px solid white",
              borderRadius: "18px",
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "10px",
              }}
            >
              <Typography variant="h6" sx={{ color: "white" }}>
                Vehicle Model
              </Typography>
              <Box>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<Add />}
                  sx={{
                    borderRadius: "20px",
                    background: "black",
                    color: "white",
                    border: "2px green solid",
                    mr: 2,
                  }}
                  onClick={() => setDialogOpen(true)}
                >
                  Add Model
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<Add />}
                  sx={{
                    borderRadius: "20px",
                    background: "black",
                    color: "white",
                    border: "2px green solid",
                  }}
                  onClick={() => setUploadDialogOpen(true)}
                >
                  Upload Bulk Data
                </Button>
              </Box>
            </Box>

            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ background: "green" }}>
                    <TableCell sx={{ color: "white", fontWeight: "bold" }}>
                      Active/Inactive
                    </TableCell>
                    <TableCell sx={{ color: "white", fontWeight: "bold" }}>
                      Model Name
                    </TableCell>
                    <TableCell sx={{ color: "white", fontWeight: "bold" }}>
                      Year
                    </TableCell>
                    <TableCell sx={{ color: "white", fontWeight: "bold" }}>
                      Vehicle Photo
                    </TableCell>
                    <TableCell sx={{ color: "white", fontWeight: "bold" }}>
                      STATUS
                    </TableCell>
                    <TableCell sx={{ color: "white", fontWeight: "bold" }}>
                      ACTION
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        <CircularProgress />
                      </TableCell>
                    </TableRow>
                  ) : models.length > 0 ? (
                    models.map((model, index) => (
                      <TableRow key={index}>
                        <StyledTableCell>
                          <Switch
                            checked={model.status}
                            onChange={() =>
                              handleStatusChangeRequest(model.id, model.status)
                            }
                            color="primary"
                          />
                        </StyledTableCell>
                        <StyledTableCell>
                          <Typography variant="body1" sx={{ color: "white" }}>
                            {model.model_name}
                          </Typography>
                        </StyledTableCell>
                        <StyledTableCell>
                          <Typography variant="body1" sx={{ color: "white" }}>
                            {new Date(model.year).getFullYear()}
                          </Typography>
                        </StyledTableCell>
                        <StyledTableCell>
                          <Avatar
                            src={model.vehicle_photo}
                            sx={{ width: 56, height: 56 }}
                          />
                        </StyledTableCell>
                        <StyledTableCell>
                          <Typography sx={{ color: "white" }}>
                            {model.status ? "Active" : "Inactive"}
                          </Typography>
                        </StyledTableCell>
                        <StyledTableCell>
                          <IconButton onClick={() => handleEditModel(model)}>
                            <Edit sx={{ color: "white" }} />
                          </IconButton>
                          <IconButton>
                            <Delete sx={{ color: "white" }} />
                          </IconButton>
                        </StyledTableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        <Typography sx={{ color: "white" }}>
                          No models found
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Pagination Buttons */}
            <Box
              display="flex"
              justifyContent="flex-end"
              mt={2}
              mb={2}
              gap={1}
              sx={{ pr: 2 }}
            >
              <Button
                variant="contained"
                onClick={handlePrevPage}
                // disabled={page === 1 || loading}
                sx={{
                  borderRadius: "20px",
                  background: "black",
                  color: "white",
                  border: "2px green solid",
                  mr: 2,
                }}
              >
                Previous
              </Button>
              <Button
                variant="contained"
                onClick={handleNextPage}
                // disabled={loading || (models.length < pageSize && page > 1)}
                sx={{
                  borderRadius: "20px",
                  background: "black",
                  color: "white",
                  border: "2px green solid",
                  mr: 2,
                }}
              >
                Next
              </Button>
            </Box>
          </Box>
        </Grid>
      </Grid>

      {/* Status Change Confirmation Dialog */}
      <Dialog
        open={statusDialogOpen}
        onClose={() => setStatusDialogOpen(false)}
      >
        <DialogTitle>Confirm Status Change</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to{" "}
            {modelToToggle?.status ? "deactivate" : "activate"} this model?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStatusDialogOpen(false)} color="secondary">
            Cancel
          </Button>
          <Button onClick={handleStatusChangeConfirm} color="primary">
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      <AddModelDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onModelAdded={handleModelAdded}
      />
      <UploadModelDialog
        open={uploaddialogOpen}
        onClose={() => setUploadDialogOpen(false)}
        onModelAdded={handleUploadModel}
      />
      {/* <EditModelDialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} model={selectedModel} onModelUpdated={handleModelUpdated} /> */}
    </Box>
  );
};

export default ModelListTable;
